import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { UserPlus, ArrowLeft } from 'lucide-react';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(formData.name, formData.email, formData.password);
      toast.success('Registration successful!');
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen noise-texture">
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-20">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Previous Page
        </button>

        <div className="max-w-md mx-auto">
          <div className="bg-white border border-border rounded-none p-12">
            <div className="text-center mb-8">
              <UserPlus className="w-16 h-16 mx-auto mb-4 text-accent" />
              <h1 className="text-4xl font-black uppercase mb-2">REGISTER</h1>
              <p className="text-sm text-muted-foreground">
                Create your account
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="uppercase tracking-wider text-xs font-bold">
                  Full Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  data-testid="register-name"
                  required
                  className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                />
              </div>

              <div>
                <Label htmlFor="email" className="uppercase tracking-wider text-xs font-bold">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="register-email"
                  required
                  className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                />
              </div>

              <div>
                <Label htmlFor="password" className="uppercase tracking-wider text-xs font-bold">
                  Password
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  data-testid="register-password"
                  required
                  className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="uppercase tracking-wider text-xs font-bold">
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  data-testid="register-confirm-password"
                  required
                  className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="register-submit"
                className="w-full bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground disabled:bg-muted disabled:text-muted-foreground rounded-none uppercase tracking-wider font-bold py-6"
              >
                {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" data-testid="goto-login" className="text-accent font-bold hover:underline">
                  Login here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
